import { Button } from "antd";
import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { contentToRow } from "../../pages/ChecklistPage/PopupEdit/ChecklistEditContext";
import { ELEMENT_ID, IPropsElement, isHasLabel } from "../../pages/ChecklistPage/PopupEdit/constants";
import { showErrorResponse } from "../../util/varibles/global";
import { IChecklist, IPropsChildren } from "../../util/varibles/interface";
import modal from "../Popup/ModalCustom";
import { ChecklistProvider, getChecklist, useChecklist } from "./ChecklistContext";
import CheckboxElement from "./Element/CheckboxElement";
import DateElement from './Element/DateElement';
import Divide from './Element/Divide';
import DropdownElement from './Element/DropdownElement';
import HeaderElement from './Element/HeaderElement';
import LongTextElement from './Element/LongTextElement';
import NumberElement from "./Element/NumberElement";
import RadioElement from './Element/RadioElement';
import ShortTextElement from './Element/ShortTextElement';
import SignElement from './Element/SignElement';
import TimeElement from './Element/TimeElement';
import styles from './style.module.scss';

const Element: React.FC<{ name?: string, verticalDivide?: boolean } & IPropsElement & IPropsChildren> = (props) => {
    const { label = "", type, index, properties: { subLabel, required } } = props;
    const isLabel = isHasLabel.has(type);
    const [error] = useChecklist(state => state.error?.[index]);

    return <div className={styles.element} data-vertical-divide={props.verticalDivide}>
        {isLabel ? <div className={styles.control}>
            <div className={styles.label}>{label} {required && <span className="color-danger">*</span>}</div>
            {props.children}
            <div className={styles.subLabel}>{subLabel}</div>
        </div> : props.children}
        {error && <div className="color-danger font-[13px]">{error}</div>}
    </div>
}


const Views: { [id: string]: React.FC<IPropsElement> } = {
    [ELEMENT_ID.CHECKBOX]: CheckboxElement,
    [ELEMENT_ID.DATE]: DateElement,
    [ELEMENT_ID.DIVIDE]: Divide,
    [ELEMENT_ID.DROPDOWN]: DropdownElement,
    [ELEMENT_ID.HEADER]: HeaderElement,
    [ELEMENT_ID.LONG_TEXT]: LongTextElement,
    [ELEMENT_ID.NUMBER]: NumberElement,
    [ELEMENT_ID.RADIO]: RadioElement,
    [ELEMENT_ID.SHORT_TEXT]: ShortTextElement,
    [ELEMENT_ID.SIGN]: SignElement,
    [ELEMENT_ID.TIME]: TimeElement,
}

interface IElementProps {
    disabled?: boolean
    cancel?: ReactNode

    onSubmit?(args: IChecklist): Promise<any>

    onClose?(): void
}

interface IProps extends IElementProps {
    checklist: IChecklist
    vesselTenantId?: string

    onResultChange?(value: any): void
}

const Elements: React.FC<IElementProps> = ({ cancel, onSubmit, onClose }) => {
    const [checklist, set] = useChecklist(state => state.data);
    const content = checklist?.content;
    const get = getChecklist();
    const [loading, setLoading] = useState(false);

    const elements = useMemo(() => {
        try {
            const value = JSON.parse(content || JSON.stringify([]));
            return contentToRow(value);
        } catch (e) {
            console.log('JSON error:', e)
            return [];
        }
    }, [content]);

    const handleSubmit = useCallback(() => {
        const { data, result } = get();
        if (!data)
            return;
        const valueDefault: Record<string, string | string[]> = {}, message: Record<string, string> = {};
        const isEmpty = Object.keys(result).length === 0;
        elements.forEach((item, i) => {
            item.forEach(sub => {
                const { label, properties: { required, optionDefault }, index } = sub;
                if (required) {
                    if (optionDefault !== undefined) {
                        valueDefault[`${index}`] = optionDefault;
                    }
                    if (isEmpty ? valueDefault[`${index}`] === undefined : result[`${index}`] === undefined) {
                        message[`${index}`] = `${label} is required`;
                    }
                }
            })
        })
        const params = { ...data, result: isEmpty ? JSON.stringify(valueDefault) : JSON.stringify(result) }
        const handleSave = () => {
            setLoading(true);
            onSubmit?.(params)
                .then(() => {
                    set({ error: {} })
                    setLoading(false);
                    onClose?.();
                })
                .catch((error) => {
                    showErrorResponse('Update failed', error);
                    setLoading(false)
                });
        }
        set({ error: message })
        if (Object.keys(message).length > 0) {
            modal.confirm2({
                title: <span className="!text-[var(--colorWarn)]">
                    Warning
                </span>,
                content: <div>
                    Some required fields are missing information. Are you sure you want to confirm?
                </div>,
                ok: {
                    click() {
                        handleSave();
                    }
                }
            })
        } else {
            handleSave();
        }
        return;
    }, [get, elements, set, onSubmit, onClose]);

    let count = 0;

    return <div className={styles.popup}>
        <div className='flex flex-col gap-[6px]'>
            {elements.map((item, i) => <div key={i + Date.now()} className='flex gap-2'>
                {item.map(sub => {
                    const { type, index = count } = sub;
                    const { [type]: View } = Views;
                    count++;
                    const props = { ...sub, index }
                    return <Element key={sub.index} {...props} verticalDivide={type === ELEMENT_ID.DIVIDE && item.length > 1}>
                        {View && <View {...props} />}
                    </Element>
                })}
            </div>)}
        </div>
        {onSubmit && <div className={styles.footer}>
            {cancel}
            <Button className='bt-primary' loading={loading} onClick={handleSubmit}>Confirm</Button>
        </div>}
    </div>;
}

const Checklist: React.FC<IProps> = (props) => {
    const { checklist, ...args } = props;

    return <ChecklistProvider
        data={checklist}
        onResultChange={props.onResultChange}
        disabled={props.disabled}
        vesselTenantId={props.vesselTenantId}
    >
        <Elements {...args} />
    </ChecklistProvider>
};

export default Checklist;
